import { useEffect } from 'react';

// WebKit-only gesture events (iOS Safari + WKWebView). Blink/Gecko never fire these, so the hook is
// a no-op on Android/desktop Chrome etc., where native zoom is disabled via the `touch-action`
// rule in globalStyles instead.
const GESTURE_EVENTS = ['gesturestart', 'gesturechange', 'gestureend'];

/**
 * Disables the browser's native pinch-zoom so it can't compete with the document viewer's own
 * zoom (react-zoom-pan-pinch). On iOS, `touch-action: pan-x pan-y` (which covers Android) does not
 * reliably suppress pinch in practice — confirmed on-device, despite Safari 13+ nominally
 * supporting touch-action — and `user-scalable` is ignored too, so on WebKit we intercept the
 * native gesture events directly.
 *
 * The `visualViewport.scale` check is an escape hatch: we only block *initiating* a zoom from the
 * unzoomed state. If the page is already zoomed — e.g. via iOS focus auto-zoom or double-tap, which
 * aren't gesture events we can intercept — the gesture is allowed through so the user can always
 * pinch back out and is never trapped.
 */
export const useDisableNativePinchZoom = (): void => {
  useEffect(() => {
    const blockZoomFromBaseline = (event: Event) => {
      const scale = window.visualViewport?.scale ?? 1;
      if (scale <= 1 && event.cancelable) {
        event.preventDefault();
      }
    };

    GESTURE_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, blockZoomFromBaseline, {
        passive: false,
      });
    });

    return () => {
      GESTURE_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, blockZoomFromBaseline);
      });
    };
  }, []);
};
